"use client";
import { useCallback, useEffect, useState } from "react";
import useSWR from "swr";
import {
	fetchCalibration,
	fetchWalletSubcategories,
	fetchWallets,
	type WalletQueryParams,
	type WalletSort,
} from "../api";
import { CalibrationCard, Explainer, SubcategoryChips, Totals } from "../components/Panels";
import { WalletTable } from "../components/WalletTable";

const SORTS: WalletSort[] = ["ev", "edge", "edge_lcb", "z", "brier", "clv", "volume", "positions", "resolved", "views"];
const MIN_OPTIONS = [0, 5, 10, 25];
const PAGE_SIZE = 25;

type State = Omit<WalletQueryParams, "limit">;

function readState(): State {
	const q = new URLSearchParams(window.location.search);
	const sort = q.get("sort") as WalletSort;
	const cat = q.get("category");
	const dir = q.get("dir");
	return {
		category: cat === "insider" || cat === "bot" ? cat : undefined,
		subcategory: q.get("sub") ?? undefined,
		sort: SORTS.includes(sort) ? sort : "edge_lcb",
		dir: dir === "asc" || dir === "desc" ? dir : undefined,
		minResolved: Math.max(0, Number.parseInt(q.get("min") ?? "5", 10) || 0),
		page: Math.max(1, Number.parseInt(q.get("page") ?? "1", 10) || 1),
	};
}

function writeState(s: State) {
	const q = new URLSearchParams();
	if (s.category) q.set("category", s.category);
	if (s.subcategory) q.set("sub", s.subcategory);
	if (s.sort !== "edge_lcb") q.set("sort", s.sort);
	if (s.dir) q.set("dir", s.dir);
	if (s.minResolved !== 5) q.set("min", String(s.minResolved));
	if (s.page > 1) q.set("page", String(s.page));
	const qs = q.toString();
	window.history.replaceState(null, "", `${window.location.pathname}${qs ? `?${qs}` : ""}`);
}

export function WalletsPage() {
	const [state, setState] = useState<State>(readState);
	const patch = useCallback((p: Partial<State>) => setState((s) => ({ ...s, page: 1, ...p })), []);
	useEffect(() => writeState(state), [state]);

	const params: WalletQueryParams = { ...state, limit: PAGE_SIZE };
	const wallets = useSWR(["wallets", params], () => fetchWallets(params), { keepPreviousData: true });
	const subs = useSWR(["walletSubs"], fetchWalletSubcategories);
	const calib = useSWR(["walletCalib"], fetchCalibration);

	const visibleSubs = (subs.data?.data ?? []).filter((s) => !state.category || s.category === state.category);
	const effectiveDir = state.dir ?? (state.sort === "brier" ? "asc" : "desc");

	const onSort = (s: WalletSort) =>
		patch({
			sort: s,
			dir: s === state.sort ? (effectiveDir === "desc" ? "asc" : "desc") : undefined,
		});

	return (
		<div className="min-h-screen bg-base-100 text-base-content" data-theme="terminal">
			<div className="max-w-7xl mx-auto p-4 md:p-6 space-y-4">
				<header className="flex flex-wrap items-end justify-between gap-2">
					<div>
						<h1 className="text-lg md:text-xl font-bold font-mono">Wallet leaderboard</h1>
						<p className="text-xs opacity-70">
							Who is flagged, what kind of trader they look like, and whether the price they paid beat the outcome.
						</p>
					</div>
					{subs.data ? <Totals items={subs.data.data} /> : null}
				</header>

				<div className="flex flex-wrap items-center gap-3">
					<div className="join">
						{([undefined, "insider", "bot"] as const).map((c) => (
							<button
								type="button"
								key={c ?? "all"}
								className={`btn btn-sm join-item ${state.category === c ? "btn-primary" : ""}`}
								onClick={() => patch({ category: c, subcategory: undefined })}
							>
								{c === undefined ? "All" : c === "insider" ? "Insiders" : "Bots"}
							</button>
						))}
					</div>
					<label className="flex items-center gap-2 text-xs font-mono">
						Min resolved
						<select
							className="select select-sm select-bordered"
							value={state.minResolved}
							onChange={(e) => patch({ minResolved: Number((e.target as HTMLSelectElement).value) })}
						>
							{MIN_OPTIONS.map((n) => (
								<option key={n} value={n}>{n === 0 ? "any" : `${n}+`}</option>
							))}
						</select>
					</label>
				</div>

				{subs.data ? (
					<SubcategoryChips items={visibleSubs} active={state.subcategory} onPick={(sub) => patch({ subcategory: sub })} />
				) : null}

				<div className="card bg-base-200 border border-base-content/10">
					<div className="card-body p-2 md:p-4">
						{wallets.error ? (
							<div className="p-4 text-sm text-error font-mono">Could not load wallets. {String(wallets.error.message ?? "")}</div>
						) : !wallets.data ? (
							<div className="p-6 text-sm font-mono opacity-60">Loading…</div>
						) : wallets.data.data.length === 0 ? (
							<div className="p-6 text-sm font-mono opacity-70">No wallets match. Try lowering “Min resolved”.</div>
						) : (
							<>
								<WalletTable rows={wallets.data.data} sort={state.sort} dir={effectiveDir} onSort={onSort} />
								<div className="flex items-center justify-between p-2 text-xs font-mono">
									<span className="opacity-70">
										{wallets.data.pagination.total} wallets · page {wallets.data.pagination.page}/{wallets.data.pagination.totalPages}
									</span>
									<div className="join">
										<button type="button" className="btn btn-xs join-item" disabled={!wallets.data.pagination.hasPrev} onClick={() => setState((s) => ({ ...s, page: s.page - 1 }))}>Prev</button>
										<button type="button" className="btn btn-xs join-item" disabled={!wallets.data.pagination.hasNext} onClick={() => setState((s) => ({ ...s, page: s.page + 1 }))}>Next</button>
									</div>
								</div>
							</>
						)}
					</div>
				</div>

				<Explainer />
				{calib.data ? <CalibrationCard buckets={calib.data.data} /> : null}
			</div>
		</div>
	);
}

export default WalletsPage;
